import type {
  drgClaimQuery,
  drgClaimStatusesQuery,
  drgCreateClaimCodeMutation,
  drgDeleteClaimCodeMutation,
  drgMasterCodesQuery,
  drgReorderClaimCodesMutation,
} from "../../remote/gql/graphql";
import type { DrgCodeKind } from "../../remote/gql/graphql";
import type { ClientReturnType } from "../types";
import type { AlaffiaClientModuleArgs } from "./modules";

import { DrgPoaValue } from "../../remote/gql/graphql";
import { drgCreateClaimCodeMutationDoc } from "../../remote/mutations/drgCreateClaimCode.gql";
import { drgDeleteClaimCodeMutationDoc } from "../../remote/mutations/drgDeleteClaimCode.gql";
import { drgEnqueueReviewMutationDoc } from "../../remote/mutations/drgEnqueueReview.gql";
import { drgRecalculateMutationDoc } from "../../remote/mutations/drgRecalculate.gql";
import { drgReorderClaimCodesMutationDoc } from "../../remote/mutations/drgReorderClaimCodes.gql";
import { getDrgClaimQueryDoc } from "../../remote/queries/getDrgClaim.gql";
import { getDrgClaimStatusesQueryDoc } from "../../remote/queries/getDrgClaimStatuses.gql";
import { getDrgMasterCodesQueryDoc } from "../../remote/queries/getDrgMasterCodes.gql";
import { AlaffiaClientModule } from "./modules";

type AprDrgModuleArgs = AlaffiaClientModuleArgs;

export class AprDrgModule extends AlaffiaClientModule {
  constructor(args: AprDrgModuleArgs) {
    super({ ...args, environment: "alafed" });
  }

  drgCreateClaimCode = async (input: {
    drgTxId: string;
    drgMasterCodeId: string;
    codeKind: DrgCodeKind;
    poa?: DrgPoaValue;
    afterDrgClaimCodeId?: string;
  }): ClientReturnType<
    NonNullable<drgCreateClaimCodeMutation["drgCreateClaimCode"]>
  > => {
    const result = await this.runGqlMutation(
      drgCreateClaimCodeMutationDoc,
      {
        input: {
          claimId: input.drgTxId,
          drgMasterCodeId: input.drgMasterCodeId,
          codeKind: input.codeKind,
          poa: input.poa ?? DrgPoaValue.U,
          afterDrgClaimCodeId: input.afterDrgClaimCodeId ?? "",
        },
      },
      "drgCreateClaimCode",
    );
    return {
      data: result.data?.drgCreateClaimCode ?? null,
      error: result.error ?? null,
    };
  };

  drgDeleteClaimCode = async (input: {
    drgTxId: string;
    drgClaimCodeId: string;
  }): ClientReturnType<
    NonNullable<drgDeleteClaimCodeMutation["drgDeleteClaimCode"]>
  > => {
    const result = await this.runGqlMutation(
      drgDeleteClaimCodeMutationDoc,
      {
        input: {
          claimId: input.drgTxId,
          drgClaimCodeId: input.drgClaimCodeId,
        },
      },
      "drgDeleteClaimCode",
    );
    return {
      data: result.data?.drgDeleteClaimCode ?? null,
      error: result.error ?? null,
    };
  };

  drgReorderClaimCodes = async (input: {
    drgTxId: string;
    drgClaimCodeIds: string[];
  }): ClientReturnType<
    NonNullable<drgReorderClaimCodesMutation["drgReorderClaimCodes"]>
  > => {
    const result = await this.runGqlMutation(
      drgReorderClaimCodesMutationDoc,
      {
        input: {
          claimId: input.drgTxId,
          drgClaimCodeIds: input.drgClaimCodeIds,
        },
      },
      "drgReorderClaimCodes",
    );
    return {
      data: result.data?.drgReorderClaimCodes ?? null,
      error: result.error ?? null,
    };
  };

  drgEnqueueReview = async (input: {
    claimId: string;
    grouperType: string;
    triggeredBy: string;
  }): ClientReturnType<{
    workflowId: string;
    alreadyRunning: boolean;
  }> => {
    const result = await this.runGqlMutation(
      drgEnqueueReviewMutationDoc,
      { input },
      "drgEnqueueReview",
    );
    return {
      data: result.data?.drgEnqueueReview ?? null,
      error: result.error ?? null,
    };
  };

  drgRecalculate = async (input: {
    claimId: string;
  }): ClientReturnType<{
    claim: Pick<
      NonNullable<drgClaimQuery["drgClaim"]>,
      | "id"
      | "drgStatus"
      | "estimatedOverpaymentCents"
      | "originalGrouping"
      | "recalculatedGrouping"
      | "codes"
    >;
  }> => {
    const result = await this.runGqlMutation(
      drgRecalculateMutationDoc,
      { claimId: input.claimId },
      "drgRecalculate",
    );
    return {
      data: result.data?.drgRecalculate ?? null,
      error: result.error ?? null,
    };
  };

  drgClaim = async (input: {
    id: string;
    includeAuditorCodes: boolean;
  }): ClientReturnType<drgClaimQuery["drgClaim"]> => {
    const result = await this.runGqlQuery(
      getDrgClaimQueryDoc,
      {
        id: input.id,
        includeAuditorCodes: input.includeAuditorCodes,
      },
      "drgClaim",
    );
    return {
      data: result.data?.drgClaim ?? null,
      error: result.error ?? null,
    };
  };

  drgClaimStatuses = async (input: {
    claimIds: string[];
  }): ClientReturnType<drgClaimStatusesQuery["drgClaimStatuses"]> => {
    const result = await this.runGqlQuery(
      getDrgClaimStatusesQueryDoc,
      { claimIds: input.claimIds },
      "drgClaimStatuses",
    );
    return {
      data: result.data?.drgClaimStatuses ?? null,
      error: result.error ?? null,
    };
  };

  drgMasterCodes = async (input: {
    drgTxId: string;
    codeKind: DrgCodeKind;
    nameInput: string;
  }): ClientReturnType<NonNullable<drgMasterCodesQuery["drgMasterCodes"]>> => {
    const result = await this.runGqlQuery(
      getDrgMasterCodesQueryDoc,
      {
        input: {
          claimId: input.drgTxId,
          codeKind: input.codeKind,
          nameInput: input.nameInput,
        },
      },
      "drgMasterCodes",
    );
    return {
      data: result.data?.drgMasterCodes ?? null,
      error: result.error ?? null,
    };
  };
}
